/**
 * @fileoverview Componente Molecule para barra de acciones del chat en el Inbox.
 * Permite acceder rápidamente a funciones como adjuntar archivos, enviar audio, notas, respuestas rápidas y productos.
 * Implementa arquitectura optimizada con separación de capas, integración modular y uso de TailwindCSS.
 */
import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RecordingAudioPanelMC from './RecordingAudioPanelMC';
import useHasMessageText from '../../../hooks/inbox/useHasMessageText';
import MessageCounterMC from './MessageCounterMC';
import IconMC from '../../../components/IconMC';
import { isTikTokPlatform } from '../../../hooks/inbox/constants/platformMessageRestrictions';

const ChatActionsBarMC = memo(({
  onToggleEmojiPicker,
  onSendMessage,
  textareaRef,
  maxChars,
  platform
}) => {
  const { t } = useTranslation('InboxMC');
  const [isRecording, setIsRecording] = useState(false);
  const hasText = useHasMessageText(textareaRef);
  // Memoizar handlers para evitar renders por cambio de referencia
  const handleToggleEmojiPicker = useCallback(() => {
    onToggleEmojiPicker?.();
  }, [onToggleEmojiPicker]);

  const handleSendMessage = useCallback(() => {
    onSendMessage?.();
  }, [onSendMessage]);

  const sendAudio = useCallback((blob) => {
    onSendMessage?.({ type: 'audio', blob });
  }, [onSendMessage]);

  return (
    <div className="tw-flex tw-h-full tw-w-auto tw-max-w-full tw-shrink-0 tw-min-w-0 tw-flex-col tw-justify-start tw-gap-1 tw-p-1 tw-pr-2 tw-text-gray-400 xl:tw-flex-row xl:tw-items-center xl:tw-justify-start xl:tw-gap-1">
      <div className="tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-end tw-gap-1 xl:tw-w-auto xl:tw-max-w-full xl:tw-justify-start xl:tw-order-2">
        {!hasText && !isTikTokPlatform(platform) && (
          <RecordingAudioPanelMC sendAudio={sendAudio} onRecordingStateChange={setIsRecording} />
        )}
        {!isRecording && (
          <div className="send-icon-tooltip" data-tooltip-content={t('chat.emoji')}>
            <IconMC
              name="smiling-face-outline"
              size="xl"
              className="hover:tw-text-gray-600 tw-transition-colors tw-duration-150 tw-cursor-pointer"
              onClick={handleToggleEmojiPicker}
            />
          </div>
        )}
        {!isRecording && hasText && (
          <div className="send-icon-tooltip" data-tooltip-content={t('chat.send')}>
            <IconMC
              name="paper-plane-outline"
              size="xl"
              className="tw-text-blue-400 hover:tw-text-blue-600 tw-transition-colors tw-duration-150 tw-cursor-pointer"
              onClick={handleSendMessage}
            />
          </div>
        )}
      </div>
      {!isRecording && (
        <div className="tw-flex tw-w-full tw-shrink-0 tw-justify-end xl:tw-order-1 xl:tw-w-auto xl:tw-justify-start">
          <MessageCounterMC textareaRef={textareaRef} maxChars={maxChars} />
        </div>
      )}
    </div>
  );
});

ChatActionsBarMC.displayName = 'ChatActionsBarMC';

export default ChatActionsBarMC;
